/* ==========================================================================
   Collection / search filters (snippets/collection-facets, drawer-filters,
   current-facets, facet-values) and sort.

   Why it was slow before: every change fetched the WHOLE page (~2.4 MB HTML,
   header + footer + every section) after a 150 ms debounce, then parsed it.
   Now:
   - Only the grid section is fetched (Section Rendering API, ?section_id=…),
     with a full-page fallback (e.g. a template whose section id differs).
   - Hovering / pressing a value prefetches the result it would give, so the
     click usually lands on a response that is already in flight or cached.
   - The UI is optimistic: checkboxes, pill counts and the URL change at once;
     the grid dims with a progress line while the response arrives. Older
     in-flight responses are ignored (latest selection wins).
   - The filter UI is updated IN PLACE (counts, checked, disabled), never
     re-rendered, so an open popover keeps its scroll position, search text
     and focus.

   The URL is updated with replaceState (filters + sort, never ?page) so Back
   from a product returns to the same filtered listing; collection-load-more.js
   keys its restore state by that URL.
   ========================================================================== */
(() => {
  if (customElements.get('facets-form')) return;

  const PREFETCH_DELAY = 70;
  const CACHE_MAX = 40;
  const cache = new Map();          // request url -> Promise<Document>
  let seq = 0;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const getGrid = () => document.getElementById('product-grid-ajax');
  const isFilterKey = (key) => key.startsWith('filter.');

  /* ---------- URL state ---------- */

  /** Current URL params minus filters and paging (keeps sort_by, q, type, …). */
  const baseParams = () => {
    const params = new URLSearchParams(location.search);
    for (const key of Array.from(params.keys())) {
      if (isFilterKey(key) || key === 'page' || key === 'section_id') params.delete(key);
    }
    return params;
  };

  /** The filter selection as rendered in the given form (one source of truth per change). */
  const paramsFromForm = (form, base = baseParams()) => {
    const params = new URLSearchParams(base);
    for (const el of form.elements) {
      if (!el.name || !isFilterKey(el.name) || el.disabled && !el.checked) continue;
      if (el.type === 'checkbox') { if (el.checked) params.append(el.name, el.value); }
      else if (el.value !== '') params.set(el.name, el.value);
    }
    return params;
  };

  const listingUrl = (params) => {
    const qs = params.toString();
    return location.pathname + (qs ? `?${qs}` : '');
  };

  const requestUrl = (params, withSection) => {
    const p = new URLSearchParams(params);
    p.sort();                                         // stable cache key
    const id = getGrid() && getGrid().dataset.id;
    if (withSection && id) p.set('section_id', id);
    return `${location.pathname}?${p.toString()}`;
  };

  /* ---------- fetching ---------- */

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function fetchDoc(params) {
    const url = requestUrl(params, true);
    if (cache.has(url)) return cache.get(url);
    const promise = (async () => {
      try {
        const res = await fetch(url, { credentials: 'same-origin', priority: 'high' });
        if (res.ok) {
          const doc = parse(await res.text());
          if (doc.getElementById('product-grid-ajax')) return doc;
        }
      } catch (e) { /* fall through to the full page */ }
      const res = await fetch(requestUrl(params, false), { credentials: 'same-origin', priority: 'high' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = parse(await res.text());
      if (!doc.getElementById('product-grid-ajax')) throw new Error('No product grid in response');
      return doc;
    })();
    cache.set(url, promise);
    promise.catch(() => cache.delete(url));
    if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
    return promise;
  }

  /* ---------- rendering ---------- */

  const forms = () => $$('[data-facets-form]');
  const valueKey = (input) => `${input.name}:${input.value}`;

  function setBusy(busy) {
    const grid = getGrid();
    if (!grid) return;
    grid.classList.toggle('is-updating', busy);
    if (busy) grid.setAttribute('aria-busy', 'true'); else grid.removeAttribute('aria-busy');
    const bar = grid.closest('[data-ajax-parent]');
    const actions = bar && $('.product-grid-actions', bar);
    if (actions) actions.classList.toggle('is-updating', busy);
  }

  /** Optimistic group summaries (pill count, "Dyr, Hav") from the checkboxes as they are now. */
  function summarise() {
    let total = 0;
    $$('[data-facet-group]').forEach((group) => {
      const checked = $$('input[type="checkbox"]:checked', group);
      const priced = $$('input[type="number"]', group).some((i) => i.value !== '');
      const count = checked.length + (priced ? 1 : 0);
      const pill = $('[data-facet-group-count]', group);
      if (pill) { pill.textContent = count; pill.hidden = count === 0; }
      const toggle = $('[data-facet-toggle]', group);
      if (toggle) toggle.classList.toggle('is-active', count > 0);
      const sel = $('[data-facet-selected]', group);
      if (sel && !priced) sel.textContent = checked.map((i) => i.closest('.fv__opt').querySelector('.fv__label').textContent.trim()).join(', ');
      if (group.closest('[data-facets-scope="sheet"]')) total += count;
    });
    $$('[data-facet-total]').forEach((el) => { el.textContent = total; el.hidden = total === 0; });
    $$('[data-facets-scope="sheet"] [data-facets-clear]').forEach((el) => { el.hidden = total === 0; });
  }

  /** Mirror one checkbox into its twin(s) in the other form (bar <-> sheet). */
  function mirror(input) {
    const key = valueKey(input);
    $$('[data-facets-form] input[type="checkbox"]').forEach((other) => {
      if (other !== input && valueKey(other) === key) other.checked = input.checked;
    });
  }

  function syncFacets(doc) {
    const fresh = new Map();
    $$('[data-facet-value]', doc).forEach((opt) => fresh.set(opt.dataset.facetValue, opt));
    $$('[data-facet-value]').forEach((opt) => {
      const next = fresh.get(opt.dataset.facetValue);
      if (!next) return;
      const input = $('input', opt);
      const nextInput = $('input', next);
      input.checked = nextInput.checked;
      input.disabled = nextInput.disabled;
      opt.classList.toggle('is-disabled', nextInput.disabled);
      const count = $('[data-facet-count]', opt);
      const nextCount = $('[data-facet-count]', next);
      if (count && nextCount && count.textContent !== nextCount.textContent) count.textContent = nextCount.textContent;
    });
    // Price inputs and group summaries come straight from the server's view.
    $$('[data-facets-form] input[type="number"]').forEach((input) => {
      const next = doc.querySelector(`input[type="number"][name="${CSS.escape(input.name)}"]`);
      if (next && document.activeElement !== input) input.value = next.value;
    });
    const nextLabel = $('[data-facets-apply-label]', doc);
    $$('[data-facets-apply-label]').forEach((el) => { if (nextLabel) el.textContent = nextLabel.textContent; });
    summarise();
  }

  function swap(selector, doc) {
    const now = $(selector);
    const next = $(selector, doc);
    if (now && next) now.innerHTML = next.innerHTML;
    return { now, next };
  }

  function render(doc) {
    const { now: grid, next } = swap('#product-grid-ajax', doc);
    if (grid && next) grid.dataset.filteredCount = next.dataset.filteredCount;
    swap('#pagination-ajax', doc);
    swap('#current-facets-ajax', doc);
    swap('#products-count-ajax', doc);
    // The response is page 1: a "load previous" control from ?page=N is stale.
    $$('.load-more-previous').forEach((el) => el.remove());
    syncFacets(doc);

    // Results start above the viewport (visitor scrolled down)? Bring the top of the grid into view, under the sticky bar.
    if (grid) {
      const actions = $('.product-grid-actions');
      const offset = actions ? Math.max(0, actions.getBoundingClientRect().bottom) : 0;
      const top = grid.getBoundingClientRect().top;
      if (top < offset) window.scrollTo({ top: window.scrollY + top - offset - 8, behavior: 'instant' });
    }
    document.dispatchEvent(new CustomEvent('facets:updated', { detail: { count: grid ? +grid.dataset.filteredCount : null } }));
  }

  async function apply(params) {
    const mine = ++seq;
    const url = listingUrl(params);
    try { history.replaceState(history.state, '', url); } catch (e) { /* throttled */ }
    setBusy(true);
    let doc;
    try {
      doc = await fetchDoc(params);
    } catch (error) {
      if (mine === seq) location.href = url;     // last resort: a normal page load
      return;
    }
    if (mine !== seq) return;                    // a newer selection is on its way
    render(doc);
    setBusy(false);
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'collection_filter', filter_params: params.toString() });
    }
  }

  /* ---------- popovers (desktop) and sheet groups (mobile) ---------- */

  let openItem = null;

  function closePopover(focusToggle) {
    if (!openItem) return;
    const toggle = $('[data-facet-toggle]', openItem);
    const panel = $('[data-facet-panel]', openItem);
    toggle.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    openItem.classList.remove('is-open');
    if (focusToggle) toggle.focus();
    openItem = null;
  }

  function openPopover(item, byKeyboard) {
    if (openItem === item) return;
    closePopover(false);
    const toggle = $('[data-facet-toggle]', item);
    const panel = $('[data-facet-panel]', item);
    panel.hidden = false;
    item.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    openItem = item;
    // Keep inside the viewport: flip to right-aligned if it would overflow.
    panel.classList.remove('is-right');
    const rect = panel.getBoundingClientRect();
    if (rect.right > document.documentElement.clientWidth - 12) panel.classList.add('is-right');
    const search = $('[data-facet-search]', panel);
    if (search && !matchMedia('(hover: none)').matches) search.focus({ preventScroll: true });
    else if (byKeyboard) { const first = $('input:not([disabled])', panel); if (first) first.focus(); }
  }

  function toggleSheetGroup(group) {
    const toggle = $('[data-facet-toggle]', group);
    const panel = $('[data-facet-panel]', group);
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
    group.classList.toggle('is-open', open);
  }

  /* ---------- search inside long lists ---------- */

  const fold = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  function filterValues(input) {
    const panel = input.closest('[data-facet-panel]');
    const q = fold(input.value.trim());
    let shown = 0;
    $$('.fv__item', panel).forEach((li) => {
      const label = fold(li.querySelector('.fv__opt').dataset.label || '');
      const hit = !q || label.includes(q);
      li.hidden = !hit;
      if (hit) shown++;
    });
    const none = $('[data-facet-search-empty]', panel);
    if (none) none.hidden = shown > 0;
  }

  /* ---------- custom elements ---------- */

  class FacetsForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.form = $('form', this);
      this.isBar = this.dataset.facetsScope === 'bar';
      this.prefetchTimer = null;

      this.form.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.name || !isFilterKey(input.name)) return;
        if (input.type === 'checkbox') mirror(input);
        summarise();
        apply(paramsFromForm(this.form));
      });

      this.form.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-facet-toggle]');
        if (!toggle) return;
        event.preventDefault();
        const item = toggle.closest('[data-facet-group]');
        if (this.isBar) {
          if (openItem === item) closePopover(false);
          else openPopover(item, event.detail === 0);
        } else {
          toggleSheetGroup(item);
        }
      });

      this.form.addEventListener('input', (event) => {
        if (event.target.matches('[data-facet-search]')) filterValues(event.target);
      });

      this.form.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('[data-facet-search]')) {
          // Enter in the search box ticks the only / first visible match.
          event.preventDefault();
          const first = $$('.fv__item:not([hidden]) input:not([disabled])', event.target.closest('[data-facet-panel]'))[0];
          if (first) first.click();
        }
        if (event.key === 'Enter' && event.target.type === 'number') {
          event.preventDefault();
          event.target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Prefetch the result a value would give, on hover (desktop) or press (touch).
      const prefetch = (opt) => {
        const input = $('input', opt);
        if (!input || input.disabled) return;
        input.checked = !input.checked;
        const params = paramsFromForm(this.form);
        input.checked = !input.checked;
        fetchDoc(params).catch(() => {});
      };
      this.form.addEventListener('pointerover', (event) => {
        if (event.pointerType !== 'mouse') return;
        const opt = event.target.closest('.fv__opt');
        if (!opt || opt.contains(event.relatedTarget)) return;
        clearTimeout(this.prefetchTimer);
        this.prefetchTimer = setTimeout(() => prefetch(opt), PREFETCH_DELAY);
      });
      this.form.addEventListener('pointerout', (event) => {
        const opt = event.target.closest('.fv__opt');
        if (opt && !opt.contains(event.relatedTarget)) clearTimeout(this.prefetchTimer);
      });
      this.form.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        const opt = event.target.closest('.fv__opt');
        if (opt) prefetch(opt);
      }, { passive: true });
    }
  }
  customElements.define('facets-form', FacetsForm);

  /* ---------- page-level listeners ---------- */

  document.addEventListener('pointerdown', (event) => {
    if (openItem && !openItem.contains(event.target)) closePopover(false);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openItem) { event.preventDefault(); closePopover(true); }
  });
  // Tabbing out closes the popover. Only real controls count: the theme moves
  // focus to <main tabindex="-1"> on some clicks, which must not close it.
  document.addEventListener('focusin', (event) => {
    if (!openItem || openItem.contains(event.target)) return;
    if (event.target.matches('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')) closePopover(false);
  });

  // Chips ("Dyr ×") and "Ryd alle" — real links without JS, in-place updates with it.
  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-facet-remove]');
    const clear = event.target.closest('[data-facets-clear]');
    if (!chip && !clear) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
    if (!forms().length) return;
    event.preventDefault();
    if (clear) {
      $$('[data-facets-form] input').forEach((input) => {
        if (input.type === 'checkbox') input.checked = false;
        else if (input.type === 'number') input.value = '';
      });
    } else {
      const keys = chip.dataset.facetRemove;
      $$('[data-facets-form] input').forEach((input) => {
        if (input.type === 'checkbox' && valueKey(input) === keys) input.checked = false;
        if (input.type === 'number' && keys.split('|').includes(input.name)) input.value = '';
      });
    }
    summarise();
    apply(paramsFromForm(forms()[0]));
  });

  // Prefetch chip removal on hover as well (after a short dwell, so sweeping
  // the pointer across the chip row does not fire a request per chip).
  let chipTimer = null;
  document.addEventListener('pointerover', (event) => {
    const chip = event.target.closest && event.target.closest('[data-facet-remove]');
    if (!chip || event.pointerType !== 'mouse' || chip.contains(event.relatedTarget)) return;
    clearTimeout(chipTimer);
    chipTimer = setTimeout(() => {
      const form = forms()[0];
      if (!form) return;
      // Same params the click will produce (see removal below), so the cache hits.
      const keys = chip.dataset.facetRemove;
      const touched = [];
      for (const input of form.elements) {
        if (input.type === 'checkbox' && input.checked && valueKey(input) === keys) { input.checked = false; touched.push([input, true]); }
        if (input.type === 'number' && input.value !== '' && keys.split('|').includes(input.name)) { touched.push([input, input.value]); input.value = ''; }
      }
      const params = paramsFromForm(form);
      touched.forEach(([input, prev]) => { if (input.type === 'checkbox') input.checked = prev; else input.value = prev; });
      fetchDoc(params).catch(() => {});
    }, 120);
  });
  document.addEventListener('pointerout', (event) => {
    const chip = event.target.closest && event.target.closest('[data-facet-remove]');
    if (chip && !chip.contains(event.relatedTarget)) clearTimeout(chipTimer);
  });

  /* ---------- sort ---------- */

  class SortBySelect extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.el = $('select', this);
      if (!this.el) return;
      const current = new URLSearchParams(location.search).get('sort_by');
      if (current) this.el.value = current;
      this.el.addEventListener('change', () => {
        const form = forms()[0];
        const params = form ? paramsFromForm(form) : (() => {
          const p = new URLSearchParams(location.search); p.delete('page'); return p;
        })();
        params.set('sort_by', this.el.value);
        apply(params);
      });
    }
  }
  if (!customElements.get('sort-by')) customElements.define('sort-by', SortBySelect);
})();

/* ---------- legacy globals (page-artwall.min.js still calls these) ---------- */
if (typeof window.generateSearchParamsFromFormData !== 'function') {
  window.generateSearchParamsFromFormData = (formElement) => {
    const keep = {};
    for (const element of formElement.elements) {
      const key = element.getAttribute('name');
      if (!key || !key.includes('filter') || element.value === '') continue;
      if (element.type === 'checkbox') {
        if (element.checked) (keep[key] = keep[key] || []).push(element.value);
        else if (Shopify.queryParams[key] && !keep[key]) delete Shopify.queryParams[key];
      } else {
        Shopify.queryParams[key] = element.value;
      }
    }
    for (const [key, value] of Object.entries(keep)) Shopify.queryParams[key] = value.join(',');
    return Shopify.queryParams;
  };
}
if (typeof window.getCollectionURLSearchParams !== 'function') {
  window.getCollectionURLSearchParams = () =>
    new URLSearchParams(Object.entries(Shopify.queryParams)).toString().replaceAll(/%2C/g, ',');
}
